import { Card, CardContent, CardOverflow, Stack, useTheme } from '@mui/joy';
import { ArrowsLeftRight, CurrencyDollar } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';

import { NumberInput } from 'core/components';
import { FlexBox, TextEllipsis } from 'core/elements';
import { Color, Direction, Level, Orientation, Variant } from 'theme/enums';

interface IProps {
  price: number | null;
  pricingMargin: number | null;
  disabled?: boolean;
  onPriceChange: (price: number | null) => void;
  onMarginChange: (margin: number | null) => void;
}

const PRICE_SLOT_PROPS = {
  input: {
    prefix: '$',
    thousandSeparator: true,
    decimalScale: 2,
  },
};

const MARGIN_SLOT_PROPS = {
  input: {
    suffix: '%',
    decimalScale: 2,
    allowNegative: true,
    max: 100,
  },
};

const parseNumeric = (raw: string): number | null => {
  if (raw === '' || raw == null) {
    return null;
  }

  const parsed = Number(raw);

  return Number.isFinite(parsed) ? parsed : null;
};

export const SellingPriceCard = ({
  price,
  pricingMargin,
  disabled,
  onPriceChange,
  onMarginChange,
}: IProps) => {
  const { t } = useTranslation();
  const { palette } = useTheme();

  const handleOnPriceChange = (event: { source: 'prop' | 'event'; target: { value: string } }) => {
    if (event.source !== 'event') {
      return;
    }
    onPriceChange(parseNumeric(event.target.value));
  };

  const handleOnMarginChange = (event: { source: 'prop' | 'event'; target: { value: string } }) => {
    if (event.source !== 'event') {
      return;
    }
    onMarginChange(parseNumeric(event.target.value));
  };

  return (
    <Card
      variant={Variant.PLAIN}
      orientation={Orientation.HORIZONTAL}
      sx={{ border: 0, flex: 1, display: 'flex', width: '100%', height: '100%' }}
    >
      <CardOverflow sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <FlexBox
          sx={{
            justifyContent: 'center',
            width: 42,
            height: 42,
            borderRadius: 6,
            backgroundColor: palette.custom.cloud,
            margin: '8px',

            '& svg': {
              width: '32px !important',
              height: '32px !important',
              color: palette.custom.velvet,
            },
          }}
        >
          <CurrencyDollar weight="fill" />
        </FlexBox>
      </CardOverflow>

      <CardContent sx={{ flex: 1 }}>
        <Stack
          direction={Direction.ROW}
          sx={{ gap: 0.5, height: '100%', alignItems: 'center', justifyContent: 'center' }}
        >
          <Stack sx={{ flex: 1, minWidth: 60 }}>
            <TextEllipsis
              content={t('labelSellingPrice')}
              maxRows={1}
              color={Color.NEUTRAL}
              level={Level.BODY_XS}
              sx={{ fontWeight: 600 }}
            />
            <NumberInput
              name="sellingPrice"
              value={price ?? ''}
              onChange={handleOnPriceChange}
              disabled={disabled}
              slotProps={PRICE_SLOT_PROPS}
            />
          </Stack>

          <ArrowsLeftRight
            style={{ color: `rgba(${palette.neutral.mainChannel} / 1)`, marginTop: '20px' }}
          />

          <Stack sx={{ flex: 1, minWidth: 60 }}>
            <TextEllipsis
              content={t('labelMargin')}
              maxRows={1}
              color={Color.NEUTRAL}
              level={Level.BODY_XS}
              sx={{ fontWeight: 600 }}
            />
            <NumberInput
              name="pricingMargin"
              value={pricingMargin ?? ''}
              onChange={handleOnMarginChange}
              disabled={disabled}
              slotProps={MARGIN_SLOT_PROPS}
            />
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};
